import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { FileText, Search, LayoutGrid, Rows3 } from 'lucide-react';
import api from '../lib/api';
import { formatIDR, formatDate, PAY_STATUS } from '../lib/format';
import { HapusCari } from '../components/HapusCari';

export default function Receipts() {
  const [list, setList] = useState(null);
  const [q, setQ] = useState('');
  const [view, setView] = useState('list');

  useEffect(() => {
    const t = setTimeout(() => {
      const params = q ? `?q=${encodeURIComponent(q)}` : '';
      api.get(`/transactions${params}`).then((r) => setList(r.data));
    }, 250);
    return () => clearTimeout(t);
  }, [q]);

  return (
    <div className="space-y-5" data-testid="receipts-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="font-display text-3xl text-ink tracking-tight">Nota</h1>
        <div className="flex gap-1.5 bg-white border border-border rounded-xl p-1" data-testid="receipt-view-toggle">
          <button data-testid="receipt-view-list" onClick={() => setView('list')}
            className={`inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-semibold transition-colors ${view === 'list' ? 'bg-brown text-white' : 'text-mocha hover:bg-ivory'}`}>
            <Rows3 size={14} /> List
          </button>
          <button data-testid="receipt-view-grid" onClick={() => setView('grid')}
            className={`inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-semibold transition-colors ${view === 'grid' ? 'bg-brown text-white' : 'text-mocha hover:bg-ivory'}`}>
            <LayoutGrid size={14} /> Kotak
          </button>
        </div>
      </div>

      <div className="relative max-w-sm">
        <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-mocha" />
        <input
          data-testid="receipt-search-input"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Cari nomor nota atau customer…"
          className="w-full rounded-xl border border-border bg-white pl-10 pr-9 py-2.5 text-sm outline-none focus:ring-2 focus:ring-brown/30"
        />
        <HapusCari show={!!q} onClear={() => setQ('')} testid="receipt-search-clear" />
      </div>

      {!list ? (
        <p className="text-sm text-mocha py-10 text-center">Memuat nota…</p>
      ) : list.length === 0 ? (
        <p className="text-sm text-mocha py-10 text-center" data-testid="receipts-empty">Belum ada nota.</p>
      ) : view === 'list' ? (
        <div className="space-y-2" data-testid="receipts-list">
          {list.map((t) => (
            <Link
              key={t.id}
              to={`/nota/${t.id}`}
              data-testid={`receipt-row-${t.transaction_number}`}
              className="bg-white rounded-xl border border-border shadow-soft px-4 py-3 flex items-center gap-3 hover:bg-ivory/60 transition-colors"
            >
              <FileText size={16} className="text-caramel shrink-0" />
              <span className="font-display font-semibold text-brown-dark text-sm whitespace-nowrap">{t.transaction_number}</span>
              <span className="text-sm font-semibold text-ink flex-1 min-w-0 truncate">{t.customer_name}</span>
              <span className="text-xs text-mocha whitespace-nowrap hidden sm:block">Pakai {formatDate(t.use_date)} • {t.pcs} pcs</span>
              <span className={`font-display text-sm whitespace-nowrap ${t.status === 'cancelled' ? 'text-mocha line-through' : 'text-ink'}`}>{formatIDR(t.total)}</span>
              <span className={`rounded-full px-2.5 py-0.5 text-[10px] font-bold whitespace-nowrap ${t.status === 'cancelled' ? 'bg-danger/10 text-danger' : PAY_STATUS[t.payment_status]?.cls}`}>
                {t.status === 'cancelled' ? 'Dibatalkan' : PAY_STATUS[t.payment_status]?.label}
              </span>
            </Link>
          ))}
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3 md:gap-4">
          {list.map((t) => (
            <Link
              key={t.id}
              to={`/nota/${t.id}`}
              data-testid={`receipt-card-${t.transaction_number}`}
              className="bg-white rounded-2xl border border-border shadow-soft p-5 card-lift block"
            >
              <div className="flex items-center justify-between">
                <span className="font-display font-semibold text-brown-dark">{t.transaction_number}</span>
                <FileText size={16} className="text-caramel" />
              </div>
              <p className="text-sm font-semibold text-ink mt-2">{t.customer_name}</p>
              <p className="text-xs text-mocha mt-0.5">Pakai {formatDate(t.use_date)} • {t.pcs} pcs</p>
              <div className="flex items-center justify-between mt-3">
                <span className={`font-display text-lg ${t.status === 'cancelled' ? 'text-mocha line-through' : 'text-ink'}`}>
                  {formatIDR(t.total)}
                </span>
                <div className="flex gap-1.5">
                  {t.status === 'cancelled' && (
                    <span data-testid={`receipt-cancelled-badge-${t.transaction_number}`} className="rounded-full px-2.5 py-0.5 text-[10px] font-bold bg-danger/10 text-danger">
                      Dibatalkan
                    </span>
                  )}
                  <span className={`rounded-full px-2.5 py-0.5 text-[10px] font-bold ${PAY_STATUS[t.payment_status]?.cls}`}>
                    {PAY_STATUS[t.payment_status]?.label}
                  </span>
                </div>
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
